import { guardDialog } from '@adornis/dialog/dialog-functions.js';

/**
 * Wire an ERM `detail.onDelete` handler for a soft delete.
 *
 * The ERM entity viewer's delete button just invokes `onDelete(entity)` with no confirmation and
 * no navigation afterwards, so we own both here: pop a confirmation dialog, run the soft-delete
 * mutation behind a loading dialog (with a success snackbar / error alert), and — only on success —
 * navigate back to the list. Detail opens push a history entry, so `history.back()` returns to it.
 *
 * Declining the confirmation or an error leaves the detail view untouched.
 */
export const confirmAndSoftDelete = async (opts: {
  /** Human label for the record type, e.g. 'partner company'. */
  entityLabel: string;
  /** Display name of the specific record, shown in the confirmation. */
  name?: string | null;
  /** Runs the actual soft-delete mutation. */
  run: () => Promise<unknown>;
}): Promise<void> => {
  const displayName = opts.name?.trim() || opts.entityLabel;
  const done = await guardDialog(opts.run, {
    hasToConfirm: true,
    confirmText: `Delete "${displayName}"? It will be removed from the list. This cannot be undone.`,
    acceptText: 'Delete',
    declineText: 'Cancel',
    highlightDecline: true,
    successText: `${opts.entityLabel} deleted`,
    errorMessagePrefix: `Could not delete ${opts.entityLabel.toLowerCase()}`,
  });
  if (done) window.history.back();
};
